export interface HelpEntry {
  title: string
  body: string
  videoCaption: string
}

export const DEFAULT_HELP: HelpEntry = {
  title: 'Help',
  body: 'Pick a view from the left drawer to see contextual help.',
  videoCaption: 'ZenBot overview · 0:42',
}

export const helpContent: Record<string, HelpEntry> = {
  voice: {
    title: 'Voice',
    body: 'Tap the Aura to start a conversation. ZenBot listens, responds, and saves a transcript automatically. Use voice when you want to think out loud — the agent summarises everything into actions after the session ends.',
    videoCaption: 'Starting a voice session · 1:24',
  },
  chat: {
    title: 'Chat',
    body: 'Have a text conversation with ZenBot powered by OpenRouter models. Switch between fast and deep models in the header, paste URLs for web search, and drop files for context. Use chat when you need a written trail or want to iterate on a draft.',
    videoCaption: 'Chatting with ZenBot · 1:48',
  },
  code: {
    title: 'Code',
    body: 'Chat with your terminal agents. Sessions are grouped by project and target — tap any row to open the full session sheet with Request, Session, and Artifacts tabs. Interrupt a running agent at any time, or use pencil and mic to send input when it\'s waiting on you.',
    videoCaption: 'Driving a code session · 2:05',
  },
  tasks: {
    title: 'Tasks',
    body: 'Review your inbox-derived task queue as Tinder-style cards. Swipe right to send, left to skip, or tap break-down to let ZenBot split a task into smaller steps. Keep this view open first thing in the morning to triage in a few minutes.',
    videoCaption: 'Triaging the task deck · 2:06',
  },
  actions: {
    title: 'Actions',
    body: 'Watch what ZenBot is currently working on in a live vertical queue. Each row shows the action, its status, and any artifact produced so far. Click an action to jump into its artifact and approve or refine it inline.',
    videoCaption: 'Action queue walkthrough · 1:37',
  },
  artifacts: {
    title: 'Artifacts',
    body: 'Browse every output ZenBot has produced — screenshots, videos, email drafts, docs, code diffs, and preview URLs. Filter by type or status, then approve, refine, or reject from the card. Every action produces exactly one artifact, so this is your single audit trail.',
    videoCaption: 'Artifact grid tour · 2:11',
  },
  transcripts: {
    title: 'Transcripts',
    body: 'Every voice session is saved and searchable here. Pick a session on the left to read the full chat-bubble transcript on the right, with timestamps and the auto-generated summary. Useful for going back to catch something you said in passing.',
    videoCaption: 'Searching transcripts · 1:29',
  },
  lists: {
    title: 'Lists',
    body: 'Lists are the atomic units that summaries compose from. Switch between Core, Standard, Computed, and Embeds tabs to see different kinds, filter by source, and manage embed permissions. Think of lists as reusable lenses over your synced data.',
    videoCaption: 'Understanding lists · 2:02',
  },
  summaries: {
    title: 'Summaries',
    body: 'Summaries are curated markdown documents composed from one or more lists. Drag list blocks to reorder them, edit the framing copy between them, and publish so the voice agent reads the updated version. This is where you decide what ZenBot knows about each domain.',
    videoCaption: 'Composing a summary · 2:34',
  },
  documents: {
    title: 'Documents',
    body: 'Collaborative document editor with sections, versions, comments, and references. Invite others to edit, roll back to any version, and link out to source material. Use documents for living artifacts that evolve over time rather than one-shot outputs.',
    videoCaption: 'Editing a document · 2:18',
  },
  library: {
    title: 'Library',
    body: 'Read-only mirror of docs synced from Confluence and Google Drive. You can add labels and annotations on top, but the content itself updates from the source. Use the library when you want ZenBot to reason over existing knowledge without copying it.',
    videoCaption: 'Library tour · 1:44',
  },
  data: {
    title: 'Data',
    body: 'Structured records pulled from Jira, QuickBooks, Calendar, and other connected sources. Click a field to edit inline — changes sync back to the source system where supported. Filter and group to build ad-hoc views without leaving ZenBot.',
    videoCaption: 'Editing records inline · 1:52',
  },
  messages: {
    title: 'Messages',
    body: 'Unified inbox pulling from Gmail and Slack so you can read everything in one place. Senders are classified as Actionable, Information, or Waiting, and threads track their own state. Reply directly or forward an item into the task queue for ZenBot to handle.',
    videoCaption: 'Unified inbox walkthrough · 2:07',
  },
  feed: {
    title: 'Feed',
    body: 'A content feed of YouTube videos and Reddit posts from sources you follow, with thumbnails and quick previews. Mark items as watched, save them to a list, or ask ZenBot to summarise a long video into bullet points. A low-stakes place to catch up.',
    videoCaption: 'Browsing the feed · 1:33',
  },
  browser: {
    title: 'Browser',
    body: 'Bookmarks panel plus an embedded browser where you can hand a URL to an agent. Type a task in the input below the page and ZenBot will drive the browser on your behalf. Handy for scraping, form-filling, and repetitive web chores.',
    videoCaption: 'Driving the browser · 2:15',
  },
  agents: {
    title: 'Agents',
    body: 'Manage the personal AI agents that act on your behalf. Each agent has Soul, Sessions, Calendar, and Actions tabs so you can tune personality, see history, and review what it did. Clone an agent to try different tones without losing the original.',
    videoCaption: 'Configuring an agent · 2:28',
  },
  workflows: {
    title: 'Workflows',
    body: 'Visual builder for multi-step automations using trigger, search, api, notify, termo, and branch step types. Connect the nodes, test with a sample input, then check run history to see every execution. Use workflows for anything you do more than twice.',
    videoCaption: 'Building a workflow · 3:04',
  },
  integrations: {
    title: 'Integrations',
    body: 'Accounts and services ZenBot has access to, all routed through Nango. Connect Gmail, Calendar, Jira, Confluence, Slack, and more — each brings its own syncs, actions, and data routing. Manage scopes, rotate credentials, and disconnect from a single place.',
    videoCaption: 'Connecting an integration · 1:52',
  },
  'source-rules': {
    title: 'Source Rules',
    body: 'Build classification and routing rules per source — Gmail, Jira, GDrive, Slack — and deploy them straight back to the source. Rules stay in sync so edits made anywhere propagate. This is where you teach ZenBot what matters from each inbox.',
    videoCaption: 'Authoring source rules · 2:21',
  },
  skills: {
    title: 'Skills',
    body: 'Browse and install capabilities from the clawhub registry — PDF extraction, transcription, web scraping, and more. Every install runs a security review and generates a contract you can approve or refine. Use this to extend what ZenBot can do beyond its built-in tools.',
    videoCaption: 'Installing a skill · 2:10',
  },
  settings: {
    title: 'Settings',
    body: 'Configure voice and model preferences, notification rules, data sync frequency, and account details. Changes take effect immediately — you do not need to restart the agent. Use the search box at the top if you cannot remember where a specific setting lives.',
    videoCaption: 'Settings overview · 1:58',
  },
  'sync-history': {
    title: 'Sync History',
    body: 'Observability for every Nango sync pipeline. Drill into a run to see which records changed, open the diff for any row, and inspect how classification rules were applied. This is the first place to look when something in Data or Messages looks stale.',
    videoCaption: 'Sync History tour · 2:13',
  },
}
